import { Box, type BoxProps } from '@chakra-ui/react'
import { LayoutGroup, motion } from 'framer-motion'
import {
  type ReactNode,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import {
  GridLayoutCollision,
  type GridLayoutPlacedItem,
} from '../GridLayoutCollision'

const LAYOUT_TRANSITION = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 38,
  mass: 0.8,
}

const MotionBox = motion.create(Box)

const trackWidthFromGrid = (
  gridWidthPx: number,
  columns: number,
  gapPx: number,
): number => (gridWidthPx - gapPx * (columns - 1)) / columns

export type GridLayoutCanvasProps = {
  items: GridLayoutPlacedItem[]
  columns: number
  gapPx?: number
  animateLayout?: boolean
  /**
   * Apply display-only viewport reflow from `items`. Disable during live
   * resize so snap preview stays on the authored track math.
   * @default true
   */
  reflow?: boolean
  /**
   * Item stays in-flow for layout/animation but is visually hidden (resize
   * overlay is shown on top).
   */
  dimmedItemId?: string
  renderItem: (item: GridLayoutPlacedItem) => ReactNode
  renderItemChrome?: (item: GridLayoutPlacedItem) => ReactNode
  registerItemRef?: (id: string, node: HTMLDivElement | null) => void
  onTrackWidthChange?: (trackWidthPx: number) => void
  /** Rendered inside the grid before items (e.g. resize silhouette). */
  beforeItems?: ReactNode
  /** Rendered inside the grid after items (e.g. absolute resize overlay). */
  afterItems?: ReactNode
} & Omit<BoxProps, 'children'>

/**
 * Shared CSS-grid surface for {@link GridLayoutView} and {@link GridLayoutEditor}.
 */
export const GridLayoutCanvas = forwardRef<
  HTMLDivElement,
  GridLayoutCanvasProps
>(function GridLayoutCanvas(
  {
    items,
    columns,
    gapPx = 24,
    animateLayout = true,
    reflow = true,
    dimmedItemId,
    renderItem,
    renderItemChrome,
    registerItemRef,
    onTrackWidthChange,
    beforeItems,
    afterItems,
    ...props
  },
  ref,
) {
  const layoutGroupId = useId()
  const localRef = useRef<HTMLDivElement | null>(null)
  const [trackWidthPx, setTrackWidthPx] = useState(0)
  const onTrackWidthChangeRef = useRef(onTrackWidthChange)

  useEffect(() => {
    onTrackWidthChangeRef.current = onTrackWidthChange
  }, [onTrackWidthChange])

  useEffect(() => {
    if (trackWidthPx > 0) onTrackWidthChangeRef.current?.(trackWidthPx)
  }, [trackWidthPx])

  useEffect(() => {
    const node = localRef.current
    if (!node) return

    const measure = (widthPx: number) => {
      const nextTrack = trackWidthFromGrid(widthPx, columns, gapPx)
      setTrackWidthPx(prev =>
        Math.abs(prev - nextTrack) < 0.5 ? prev : nextTrack,
      )
    }

    measure(node.getBoundingClientRect().width)

    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return
      measure(entry.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [columns, gapPx])

  const displayItems =
    !reflow || trackWidthPx <= 0
      ? items
      : GridLayoutCollision.reflowForViewport(
          items,
          columns,
          trackWidthPx,
          gapPx,
        )

  const setRefs = (node: HTMLDivElement | null) => {
    localRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }

  return (
    <LayoutGroup id={layoutGroupId}>
      <Box
        ref={setRefs}
        position="relative"
        display="grid"
        alignItems="stretch"
        gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
        gridAutoRows="minmax(min-content, auto)"
        gap={`${gapPx}px`}
        {...props}
      >
        {beforeItems}
        {displayItems.map(item => {
          const rowSpan = item.rowSpan ?? 1
          const dimmed = dimmedItemId != null && item.id === dimmedItemId

          return (
            <MotionBox
              key={item.id}
              ref={(node: HTMLDivElement | null) => {
                registerItemRef?.(item.id, node)
              }}
              layout={animateLayout}
              layoutDependency={`${item.col}:${item.row}:${item.colSpan}:${rowSpan}`}
              transition={{ layout: LAYOUT_TRANSITION }}
              position="relative"
              gridColumn={`${item.col + 1} / span ${item.colSpan}`}
              gridRow={`${item.row + 1} / span ${rowSpan}`}
              alignSelf="stretch"
              zIndex={dimmed ? 0 : 1}
              minW={0}
              display="flex"
              flexDirection="column"
              opacity={dimmed ? 0 : 1}
              pointerEvents={dimmed ? 'none' : undefined}
              aria-hidden={dimmed || undefined}
            >
              <Box
                flex="1"
                minW={0}
                minH={0}
                display="flex"
                flexDirection="column"
              >
                {renderItem(item)}
              </Box>
              {dimmed ? null : renderItemChrome?.(item)}
            </MotionBox>
          )
        })}
        {afterItems}
      </Box>
    </LayoutGroup>
  )
})

export { LAYOUT_TRANSITION, MotionBox, trackWidthFromGrid }
